import { useId } from 'react';
import {
  UNIDADES_DE_PERIODO,
  unidadeValida,
  validarPeriodo,
  type PeriodoParcial,
  type UnidadeDePeriodo,
} from '@/lib/periodo';

// Campo de Período (componente 3.1 da especificação).
//
// Construção única, como o bloco de endereço: toda pergunta de duração no
// módulo de compliance usa este componente — prazo e vigência do treinamento,
// prazo e vigência do alvará, periodicidade de manutenção do item, tempo médio
// de emissão do documento.
//
// Não é enfeite. Cada tela que desenhasse os próprios dois controles escolheria
// a própria ordem, o próprio rótulo e o próprio critério de "vazio", e a
// diferença apareceria como bug: um formulário gravando `{3, null}`, que a
// constraint do banco recusa, enquanto o vizinho grava `{null, null}`, que ela
// aceita.

interface Props {
  rotulo: string;
  valor: PeriodoParcial;
  onChange: (periodo: PeriodoParcial) => void;
  /** Texto de apoio: para que serve este prazo, nesta tela. */
  ajuda?: string;
  /**
   * O que "em branco" significa aqui. Vazio é um estado legítimo — item sem
   * manutenção recorrente, alvará que não vence —, mas o que ele quer dizer
   * muda com o campo, e a tela é o único lugar que sabe.
   */
  textoVazio?: string;
  obrigatorio?: boolean;
  desabilitado?: boolean;
  /** Erro vindo de fora (validação do formulário inteiro, recusa do banco). */
  erro?: string | null;
}

const ROTULO_DA_UNIDADE: Record<UnidadeDePeriodo, string> = {
  dias: 'dia(s)',
  meses: 'mês(es)',
  anos: 'ano(s)',
};

const CLASSE_BASE =
  'px-3 py-2.5 rounded-md border bg-background-50 text-sm text-foreground-900 disabled:opacity-60';

export default function CampoPeriodo({
  rotulo,
  valor,
  onChange,
  ajuda,
  textoVazio,
  obrigatorio = false,
  desabilitado = false,
  erro = null,
}: Props) {
  const id = useId();
  const problema = erro ?? validarPeriodo(valor);
  const borda = problema ? 'border-red-400' : 'border-background-300';

  const alterarValor = (texto: string) => {
    const limpo = texto.replace(/\D/g, '');
    if (limpo === '') {
      // Apagar o número apaga o Período inteiro. Deixar a unidade sozinha
      // produziria a metade que a constraint recusa, e a pessoa não veria por
      // que o formulário não salva.
      onChange({ valor: null, unidade: null });
      return;
    }
    onChange({
      valor: Number(limpo),
      // Escolher o número sem escolher a unidade é o caso comum: assume meses,
      // que é a duração mais frequente no catálogo, e a pessoa troca se quiser.
      unidade: valor.unidade ?? 'meses',
    });
  };

  const alterarUnidade = (texto: string) => {
    onChange({ valor: valor.valor, unidade: unidadeValida(texto) ? texto : null });
  };

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-foreground-700 mb-1.5">
        {rotulo}
        {obrigatorio && <span className="text-red-500 ml-0.5">*</span>}
      </label>

      <div className="flex gap-2">
        <input
          id={id}
          type="text"
          inputMode="numeric"
          value={valor.valor ?? ''}
          onChange={(e) => alterarValor(e.target.value)}
          disabled={desabilitado}
          placeholder="—"
          aria-invalid={problema ? true : undefined}
          aria-describedby={problema ? `${id}-erro` : ajuda ? `${id}-ajuda` : undefined}
          className={`${CLASSE_BASE} ${borda} w-24`}
        />
        <select
          value={valor.unidade ?? ''}
          onChange={(e) => alterarUnidade(e.target.value)}
          disabled={desabilitado}
          aria-label={`Unidade de ${rotulo.toLowerCase()}`}
          className={`${CLASSE_BASE} ${borda} flex-1`}
        >
          <option value="">Sem prazo definido</option>
          {UNIDADES_DE_PERIODO.map((u) => (
            <option key={u} value={u}>
              {ROTULO_DA_UNIDADE[u]}
            </option>
          ))}
        </select>
      </div>

      {problema ? (
        <p id={`${id}-erro`} role="alert" className="text-xs text-red-600 mt-1">
          {problema}
        </p>
      ) : (
        (ajuda || textoVazio) && (
          <p id={`${id}-ajuda`} className="text-xs text-foreground-500 mt-1">
            {ajuda}
            {ajuda && textoVazio ? ' ' : ''}
            {textoVazio && <span className="italic">Em branco: {textoVazio}</span>}
          </p>
        )
      )}
    </div>
  );
}
